/**
 * GRN composer · section 2 — Vendor (auto-populated from the selected PO).
 *
 * Faithful Tailwind translation of the prototype's section 2 markup:
 *   - `.vendor-card`    → violet avatar + name-row (name + GSTIN-verified pill)
 *                         + two `.meta` lines (GSTIN · state · city; contact).
 *   - `.vendor-history` → 3-cell strip (POs with us · Delivery record · Last
 *                         receipt). V1 has no PO/GRN-history aggregation on the
 *                         wire yet, so these render as honest dashes rather than
 *                         the prototype's demo figures.
 *
 * Unlike the PO composer this section is fully read-only — the vendor is
 * whatever the chosen PO is for; there's no picker here. Before a PO is
 * selected, an honest "select a PO" placeholder stands in.
 *
 * @see prototype/purchases/goods-receipt-new.html §"2 · Vendor"
 */
import { Check } from "lucide-react";
import { GrnSectionCard } from "./grn-section-card.js";

export interface GrnVendor {
  name: string;
  gstin: string | null;
  state: string | null;
  city: string | null;
}

interface GrnVendorSectionProps {
  poNumber: string | null;
  vendor: GrnVendor | null;
}

export function GrnVendorSection({ poNumber, vendor }: GrnVendorSectionProps) {
  const stateCode = vendor?.gstin?.slice(0, 2) ?? "";

  return (
    <GrnSectionCard
      step="2"
      title="Vendor"
      description={
        poNumber ? `Auto-populated from ${poNumber}.` : "Auto-populated from the purchase order."
      }
    >
      {vendor ? (
        <div className="grid gap-3.5">
          {/* .vendor-card */}
          <div className="flex items-start gap-3.5 rounded-sm border border-line bg-raised p-3.5">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-violet-600 text-sm font-bold text-white">
              {vendorInitials(vendor.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-ink">{vendor.name}</span>
                {vendor.gstin ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-px text-xs font-semibold text-emerald-700">
                    <Check className="h-2.5 w-2.5" strokeWidth={2.4} />
                    GSTIN verified
                  </span>
                ) : null}
              </div>
              <div className="text-xs leading-relaxed text-muted">
                <strong className="font-medium text-ink-2">
                  {vendor.gstin ?? "GSTIN not on file"}
                </strong>
                {vendor.state ? ` · ${vendor.state}${stateCode ? ` (${stateCode})` : ""}` : ""}
                {vendor.city ? ` · ${vendor.city}` : ""}
              </div>
            </div>
          </div>

          {/* .vendor-history — honest placeholders; no PO/GRN-history wire yet */}
          <div className="grid grid-cols-3 gap-2 rounded-sm border border-line bg-paper px-3 py-2.5">
            <VendorHistoryCell label="POs with us" value="—" />
            <VendorHistoryCell label="Delivery record" value="—" />
            <VendorHistoryCell label="Last receipt" value="—" />
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted">
          Select a purchase order in step 1 — the vendor fills in automatically.
        </p>
      )}
    </GrnSectionCard>
  );
}

function VendorHistoryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-xs font-semibold text-ink">{value}</div>
    </div>
  );
}

function vendorInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? words[0]?.[1] ?? "")).toUpperCase();
}
